import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'components/button'
import { Box, Flex } from 'components/elements'
import { ErrorBox } from 'features/profile/components/EditProfileForm'
import { Controller, FieldError, useForm } from 'react-hook-form'
import { FormData, QuoteProps, Service } from 'features/quote/components/type'
import { Stages } from 'utils/enum'
import { CustomRadioButton, ErrorCSS, Form, Label, userRegHeadingCss } from 'views/style'
import _ from 'lodash'
import { useGetPlanListingForQuote } from 'features/quote/api/getPlanListingForQuote'
import { useEffect, useMemo, useState } from 'react'
import { QouteLayout } from 'features/quote/page/GetAQuotes'
import { BackNavArrowIcon } from 'components/icons'
import { step2Schema } from 'features/quote/components/GetAQuoteProcess/CoveragePlan'
import { Period } from 'configs/constants'
import { usePlanDiscountCalculation } from 'features/insurance/api/planDiscountCalculation'
import { CircularProgress } from '@mui/material'
import { AxiosError } from 'axios'
import { errorMessage, getUpperCase } from 'utils/function'
import { TERMINOLOGY } from 'utils/enum'

const PolicyCoveragePlan = (props: QuoteProps) => {
  const { setStage, quoteUserReg, setQuoteUserReg, setOpenModal } = props
  const [submitError, setSubmitError] = useState('')

  const planId = props?.quoteUserReg?.planId
  const { data: planList, isLoading } = useGetPlanListingForQuote({ id: planId })
  const { mutate, isLoading: isCalculatingDiscount } = usePlanDiscountCalculation()
  const coverageRange = planList?.firstPlan?.coveragePlans
  //user selected plan details
  const planInfo = planList?.firstPlan

  if (coverageRange) {
    coverageRange.sort((a: { maxCoverage: number }, b: { maxCoverage: number }) => a.maxCoverage - b.maxCoverage)
  }
  const maxCoverageRange: Service[] = coverageRange?.map((item: { maxCoverage: string }) => {
    const coverage = parseFloat(item.maxCoverage).toLocaleString()

    // Format the number with thousands separators
    return {
      label: `$${coverage}`,
      value: `${item.maxCoverage}`,
    }
  })
  // remove the duplicates from maxCoverageRange
  const uniqueMaxCoverageRange = maxCoverageRange?.reduce((acc: Service[], curr: Service) => {
    const existingItem = acc.find((item: Service) => item.value === curr.value)
    if (!existingItem) {
      acc.push(curr)
    }
    return acc
  }, [])

  const cLookup = useMemo(() => {
    const map = {} as any
    const coveragePlans = coverageRange ?? []
    coveragePlans.forEach((item: any) => {
      map[item.maxCoverage] = item
    })
    return map
  }, [coverageRange])

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(step2Schema),
    defaultValues: {
      coverage: (quoteUserReg && quoteUserReg.coverage?.toString()) || '',
      period: quoteUserReg?.period,
    },
  })
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  const coverage = watch('coverage')
  const plan = cLookup[coverage]

  const hasMonthlyPremium = plan?.monthPremium !== null && plan?.monthPremium !== undefined

  useEffect(() => {
    if (!plan) return

    // If only one payment period is available, preselect it to avoid blocking the flow.
    if (!hasMonthlyPremium) setValue('period', Period.annual, { shouldValidate: true })
  }, [plan, hasMonthlyPremium, setValue])

  const onSubmit = async (data: FormData) => {
    if (!plan) return
    setSubmitError('')
    const quoteUserUpdateObj = {
      ...props.quoteUserReg,
      coverageId: plan.id,
      period: data.period,
      coverage: data.coverage,
      planName: planInfo?.name,
    }
    setQuoteUserReg?.(quoteUserUpdateObj)
    mutate(
      { ...quoteUserUpdateObj, paymentPeriod: data.period, services: quoteUserReg?.service },
      {
        onSuccess: data => {
          const responseObj = data?.data
          //@ts-expect-error improve type
          setQuoteUserReg?.(prev => ({
            ...prev,
            premiumInfo: {
              premium: (responseObj?.premium ?? 0).toLocaleString(),
              maxCoverage: (responseObj?.maxCoverage ?? 0).toLocaleString(),
              discountPercent: responseObj?.discountedPercentage ?? 0,
              discountValue: (responseObj?.discountedPremium ?? 0).toLocaleString(),
              period: responseObj?.paymentPeriod ?? '',
            },
          }))
          setStage?.(Stages.STAGE4)
        },
        onError: error => {
          if (error instanceof AxiosError) {
            setSubmitError(errorMessage(error))
          }
        },
      }
    )
  }
  const chunkedAs = _.chunk(uniqueMaxCoverageRange, 4)

  return (
    <>
      {isLoading ? (
        <Flex justify="center" align="center" css={{ m: '6rem' }}>
          <CircularProgress size={24} sx={{ color: 'var(--colors-advantage) !important' }} />
        </Flex>
      ) : (
        <>
          <Flex align="center">
            <BackNavArrowIcon fontSize={24} style={{ cursor: 'pointer' }} onClick={() => setStage && setStage(Stages.STAGE2)} />
            <Flex as="h2" justify={'center'} css={userRegHeadingCss}>
              Change {TERMINOLOGY.warranty.singular}
            </Flex>
          </Flex>
          <Flex align="start" css={QouteLayout}>
            <Form onSubmit={handleSubmit(onSubmit)} css={{ width: '100%' }}>
              {!!submitError && <ErrorBox css={{ textAlign: 'left', mb: '$3' }}>{getUpperCase(submitError)}</ErrorBox>}

              <Box css={{ textAlign: 'left' }}>
                <Box css={{ fontSize: '1rem', mb: '1rem' }}>Please Select Ransomware {TERMINOLOGY.benefit.singular}</Box>
                <Flex direction={'row'} wrap={'wrap'} justify={'between'} css={{ maxWidth: '500px', width: '100%', columnGap: '20px' }}>
                  {chunkedAs.map((row, rowIndex) => (
                    <Box key={rowIndex} css={{ flex: 1 }}>
                      {row.map((service: Service, index) => (
                        <Box key={index}>
                          <Label key={service.value} css={{ alignItems: 'center' }}>
                            <Controller
                              name="coverage"
                              control={control}
                              render={({ field }) => (
                                <CustomRadioButton
                                  type="radio"
                                  value={service.value}
                                  checked={field.value === service.value}
                                  onChange={e => {
                                    field.onChange(e.target.value)
                                  }}
                                  data-state={field.value === service.value ? 'checked' : 'unchecked'}
                                />
                              )}
                            />
                            {service.label}
                          </Label>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Flex>

                {!!errors.coverage && <ErrorBox css={{ ...ErrorCSS, textAlign: 'left' }}>{(errors.coverage as FieldError).message}</ErrorBox>}
              </Box>
              {plan && (
                <div>
                  <Box css={{ fontSize: '1rem', mb: '1rem', textAlign: 'left' }}>Please Select Payment Period</Box>
                  <Flex direction={'row'} wrap={'wrap'} justify={'between'} css={{ maxWidth: '500px', width: '100%', columnGap: '20px' }}>
                    {hasMonthlyPremium && (
                      <Label css={{ flex: 1, alignItems: 'center' }}>
                        <Controller
                          name="period"
                          control={control}
                          render={({ field }) => (
                            <CustomRadioButton
                              type="radio"
                              value={Period.month}
                              checked={field.value === Period.month}
                              onChange={e => {
                                field.onChange(e.target.value)
                              }}
                              data-state={field.value === Period.month ? 'checked' : 'unchecked'}
                            />
                          )}
                        />
                        <Box>Monthly (${plan.monthPremium?.toLocaleString()})</Box>
                      </Label>
                    )}
                    <Label css={{ flex: 1, alignItems: 'center' }}>
                      <Controller
                        name="period"
                        control={control}
                        render={({ field }) => (
                          <CustomRadioButton
                            type="radio"
                            value={Period.annual}
                            checked={field.value === Period.annual}
                            onChange={e => {
                              field.onChange(e.target.value)
                            }}
                            data-state={field.value === Period.annual ? 'checked' : 'unchecked'}
                          />
                        )}
                      />
                      <Box>Annual (${plan.annualPremium?.toLocaleString()})</Box>
                    </Label>
                  </Flex>
                  {!!errors.period && <ErrorBox css={{ ...ErrorCSS, textAlign: 'left' }}>{(errors.period as FieldError).message}</ErrorBox>}
                </div>
              )}

              <ChangePolicyFormButtons setOpenModal={setOpenModal} setStage={setStage} setQuoteUserReg={setQuoteUserReg} isLoading={isCalculatingDiscount} />
            </Form>
          </Flex>
        </>
      )}
    </>
  )
}

export const ChangePolicyFormButtons = (props: QuoteProps) => {
  const { setOpenModal, setStage, setQuoteUserReg, isLoading } = props
  const handleCancel = () => {
    setQuoteUserReg?.({})
    setOpenModal?.({ label: '', isOpen: false })
    setStage?.(Stages.STAGE1)
  }
  return (
    <Flex justify={'center'} wrap={'wrap'} css={{ gap: 28 }}>
      <Button size="sm" bordered={true} type="button" onClick={handleCancel}>
        CANCEL
      </Button>
      <Button size="sm" type="submit">
        {isLoading ? <CircularProgress size={18} sx={{ color: 'var(--colors-white) !important' }} /> : 'NEXT'}
      </Button>
    </Flex>
  )
}
export default PolicyCoveragePlan
